import {
  Component,
  Input,
  Output,
  EventEmitter,
  forwardRef,
  ContentChild,
  TemplateRef,
  Directive,
  ChangeDetectionStrategy,
  HostBinding,
  signal,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import {
  ControlValueAccessor,
  NG_VALUE_ACCESSOR,
  NG_VALIDATORS,
  AbstractControl,
  ValidationErrors,
  Validator,
} from '@angular/forms';

// ─── Types — alignés sur RadioBox ────────────────────────────────────────────

export type CheckboxSize    = 'small' | 'medium' | 'large';
export type CheckboxColor   =
  | 'amarante' | 'moonstone' | 'rose' | 'mint'
  | 'semantic-red' | 'semantic-yellow' | 'semantic-green';
export type CheckboxState   = 'default' | 'disabled';
export type CheckboxIconPos = 'left' | 'right' | 'none';

// ─── Directives icônes ────────────────────────────────────────────────────────

/** Icône affichée dans l'état default et hover */
@Directive({ selector: '[appCheckboxIcon]', standalone: true })
export class CheckboxIconDirective {
  constructor(public templateRef: TemplateRef<any>) {}
}

/** Icône affichée quand la case est cochée (remplace appCheckboxIcon) */
@Directive({ selector: '[appCheckboxIconChecked]', standalone: true })
export class CheckboxIconCheckedDirective {
  constructor(public templateRef: TemplateRef<any>) {}
}

// ─── CheckboxComponent ────────────────────────────────────────────────────────

let nextCheckboxId = 0;

@Component({
  selector:    'app-checkbox',
  standalone:  true,
  imports:     [CommonModule],
  templateUrl: './checkbox.component.html',
  styleUrls:   ['./checkbox.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [
    {
      provide:     NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => CheckboxComponent),
      multi:       true,
    },
    {
      provide:     NG_VALIDATORS,
      useExisting: forwardRef(() => CheckboxComponent),
      multi:       true,
    },
  ],
})
export class CheckboxComponent implements ControlValueAccessor, Validator {

  // ── Apparence ────────────────────────────────────────────────────────────────
  @Input() size:         CheckboxSize    = 'medium';
  @Input() color:        CheckboxColor   = 'amarante';
  @Input() state:        CheckboxState   = 'default';
  @Input() iconPosition: CheckboxIconPos = 'none';

  // ── Contenu ──────────────────────────────────────────────────────────────────
  @Input() label = '';

  // ── Validation ───────────────────────────────────────────────────────────────
  @Input() required     = false;
  @Input() errorMessage = 'Ce champ est obligatoire';

  // ── Comportement ─────────────────────────────────────────────────────────────
  @Input() fullWidth = false;

  // ── Accessibilité ────────────────────────────────────────────────────────────
  /** Nom accessible de la case quand elle n'a pas de `label` visible. */
  @Input() ariaLabel = '';

  /** Relie le nom accessible de la case au texte de son `label`. */
  readonly labelId = `app-checkbox-label-${nextCheckboxId++}`;

  @HostBinding('style.width')
  get hostWidth(): string { return this.fullWidth ? '100%' : ''; }

  // ── Output ───────────────────────────────────────────────────────────────────
  @Output() changed = new EventEmitter<boolean>();

  // ── Icônes ───────────────────────────────────────────────────────────────────
  @ContentChild(CheckboxIconDirective)
  iconDir?: CheckboxIconDirective;

  @ContentChild(CheckboxIconCheckedDirective)
  iconCheckedDir?: CheckboxIconCheckedDirective;

  get iconTpl(): TemplateRef<any> | null {
    return this.iconDir?.templateRef ?? null;
  }

  get iconCheckedTpl(): TemplateRef<any> | null {
    return this.iconCheckedDir?.templateRef ?? null;
  }

  /** Icône courante : cochée → iconChecked si fourni, sinon icon */
  get activeIconTpl(): TemplateRef<any> | null {
    return (this.checked() && this.iconCheckedTpl)
      ? this.iconCheckedTpl
      : this.iconTpl;
  }

  // ── State interne (signals Angular 21) ───────────────────────────────────────
  readonly checked   = signal(false);
  readonly isTouched = signal(false);

  // ── CVA ──────────────────────────────────────────────────────────────────────
  private _onChange:  (v: boolean) => void = () => {};
  private _onTouched: ()           => void = () => {};

  writeValue(val: boolean): void {
    this.checked.set(!!val);
  }
  registerOnChange(fn: any):  void { this._onChange = fn; }
  registerOnTouched(fn: any): void { this._onTouched = fn; }
  setDisabledState(disabled: boolean): void {
    this.state = disabled ? 'disabled' : 'default';
  }

  // ── Validator ─────────────────────────────────────────────────────────────────
  validate(_: AbstractControl): ValidationErrors | null {
    if (!this.required) return null;
    return this.checked() ? null : { required: true };
  }

  // ── Helpers ──────────────────────────────────────────────────────────────────

  get isDisabled(): boolean { return this.state === 'disabled'; }

  get showError(): boolean {
    return this.required && this.isTouched() && !this.checked();
  }

  get boxClasses(): Record<string, boolean> {
    return {
      'checkbox-box':                  true,
      [`checkbox-box--${this.size}`]:  true,
      [`checkbox-box--${this.color}`]: true,
      'checkbox-box--checked':         this.checked(),
      'checkbox-box--disabled':        this.isDisabled,
      'checkbox-box--error':           this.showError,
      'checkbox-box--icon-left':       this.iconPosition === 'left',
      'checkbox-box--icon-right':      this.iconPosition === 'right',
      'checkbox-box--full-width':      this.fullWidth,
    };
  }

  /**
   * Espace, comme une case native. Le carré est l'élément focalisable (rôle checkbox) ; le libellé, lui, reste
   * cliquable à la souris. Sans `preventDefault`, la page défilerait. Maintenue, la touche répète `keydown` : seul le
   * premier appui bascule la case, comme une case native qui ne bascule qu'une fois par appui.
   */
  onSpace(event: Event): void {
    event.preventDefault();
    if ((event as KeyboardEvent).repeat) return;

    this.onToggle();
  }

  onToggle(): void {
    if (this.isDisabled) return;
    this.isTouched.set(true);
    this._onTouched();
    this.checked.update(v => !v);
    this._onChange(this.checked());
    this.changed.emit(this.checked());
  }
}
